"use client";

import { Loader } from "@/components/motion/loader";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Square } from "lucide-react";
import { useLayoutEffect, useRef, type ReactNode, type Ref } from "react";

export interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  pending?: boolean;
  hint?: ReactNode;
  submitLabel?: string;
  onStop?: () => void;
  stopLabel?: string;
  autoFocus?: boolean;
  autoGrow?: boolean;
  rows?: number;
  textareaRef?: Ref<HTMLTextAreaElement>;
  className?: string;
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === "function") ref(value);
  else (ref as { current: T | null }).current = value;
}

export function ChatComposer({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled = false,
  pending = false,
  hint = "Press Enter to send",
  submitLabel = "Send",
  onStop,
  stopLabel = "Stop",
  autoFocus = false,
  autoGrow = false,
  rows = 3,
  textareaRef,
  className,
}: ChatComposerProps) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const blocked = disabled || pending;
  const stopMode = Boolean(onStop);
  const submitDisabled = value.trim() === "" || blocked || stopMode;

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!autoGrow || !el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [autoGrow, value]);

  const submit = () => {
    if (submitDisabled) return;
    onSubmit(value);
  };

  return (
    <div className={cn("rounded-lg border bg-background", className)}>
      <textarea
        ref={(node) => {
          innerRef.current = node;
          assignRef(textareaRef, node);
        }}
        placeholder={placeholder}
        rows={rows}
        value={value}
        autoFocus={autoFocus}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (
            event.key === "Enter" &&
            !event.shiftKey &&
            !event.nativeEvent.isComposing
          ) {
            event.preventDefault();
            submit();
          }
        }}
        className={cn(
          "w-full resize-none bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0",
          autoGrow && "max-h-48 overflow-y-auto",
        )}
      />
      <div className="flex min-w-0 items-center justify-between gap-2 border-t px-3 py-2">
        <span className="min-w-0 truncate text-xs text-muted-foreground">
          {hint}
        </span>
        {stopMode ? (
          <Button
            type="button"
            size="sm"
            className="h-7 shrink-0 px-3"
            disabled={pending}
            aria-busy={pending || undefined}
            aria-label={stopLabel}
            title={stopLabel}
            onClick={onStop}
          >
            {pending ? (
              <Loader variant="dither" size={14} />
            ) : (
              <Square className="size-3.5 fill-current" />
            )}
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            className="h-7 shrink-0 px-3"
            disabled={submitDisabled}
            aria-busy={pending || undefined}
            onClick={submit}
          >
            {pending ? <Loader variant="dither" size={14} /> : submitLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
